import { useEffect, useState, useCallback } from 'react';
import { query, collection, where, orderBy, limit, onSnapshot, Query, DocumentData, getFirestore } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { intlLocale } from '../i18n/locale';
import i18n from '../i18n';

// Firebase config - same as in src/auth/firebase.ts
const firebaseConfig = {
  apiKey: "AIzaSyA7MiLqUGmtI25eZCVMZdNno3LxzFZvKfU",
  authDomain: "rsmaidsbooking.firebaseapp.com",
  projectId: "rsmaidsbooking",
  storageBucket: "rsmaidsbooking.firebasestorage.app",
  messagingSenderId: "286713574927",
  appId: "1:286713574927:web:7045f1d68c9c4f17fc157a",
  measurementId: "G-Y44MRN2FL5"
};

const firebaseApp = initializeApp(firebaseConfig);

/**
 * Timeline Event from Firestore
 * Unified inbox combines SMS, calls, Wix messages, and system events
 */
export interface TimelineEvent {
  id: string;
  customerId: string;
  type: 'sms' | 'call' | 'wixMessage' | 'system';
  referenceId: string;
  direction?: 'inbound' | 'outbound';
  preview: string;
  timestamp: number;
  channel: 'sms' | 'call' | 'wix' | 'system';
  staffId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Call Log from Firestore
 */
export interface CallLog {
  id?: string;
  callId: string;
  twilioCallSid: string;
  mode: 'voip' | 'callthrough';
  direction: 'inbound' | 'outbound';
  staffId: string;
  customerId?: string;
  customerNumber: string;
  businessNumber?: string;
  status: 'pending' | 'ringing' | 'active' | 'completed' | 'failed' | 'no-answer' | 'busy';
  timestamp: number;
  startedAt: string;
  answeredAt?: string;
  endedAt?: string;
  duration?: number;
  recordingUrl?: string;
  recordingDuration?: number;
  transcriptionText?: string;
}

/**
 * SMS Message from Firestore
 */
export interface SmsMessage {
  id?: string;
  smsId: string;
  customerId: string;
  direction: 'inbound' | 'outbound';
  fromNumber: string;
  toNumber: string;
  body: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'received';
  timestamp: number;
}

/**
 * Wix Message from Firestore
 */
export interface WixMessage {
  id?: string;
  sender: 'customer' | 'staff';
  body: string;
  timestamp: number;
}

/**
 * Unified Inbox Item (fully resolved)
 */
export interface UnifiedInboxItem {
  id: string;
  customerId: string;
  type: 'sms' | 'call' | 'wixMessage' | 'system';
  direction?: 'inbound' | 'outbound';
  preview: string;
  timestamp: number;
  timestamp_display: string; // Human readable
  channel: 'sms' | 'call' | 'wix' | 'system';
  staffId?: string;
  
  // Resolved content
  sms?: SmsMessage;
  call?: CallLog;
  wixMessage?: WixMessage;
  systemMessage?: string;
  
  // Metadata
  icon: string;
  label: string;
  color: string;
}

export interface UseUnifiedInboxOptions {
  customerId?: string;
  days?: number;
  limit?: number;
  autoSubscribe?: boolean;
}

export interface UseUnifiedInboxResult {
  items: UnifiedInboxItem[];
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch and subscribe to unified inbox timeline
 * Combines SMS, calls, Wix messages, and system events
 */
export function useUnifiedInbox(options: UseUnifiedInboxOptions = {}): UseUnifiedInboxResult {
  const {
    customerId,
    days = 30,
    limit: resultLimit = 50,
    autoSubscribe = true,
  } = options;

  const [items, setItems] = useState<UnifiedInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Format timestamp to human-readable format
   */
  const formatTimestamp = useCallback((timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return i18n.t('hooks.useUnifiedInbox.now');
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString(intlLocale());
  }, []);

  /**
   * Get icon and label for timeline event type
   */
  const getEventMetadata = useCallback((event: TimelineEvent): { icon: string; label: string; color: string } => {
    switch (event.type) {
      case 'sms':
        return {
          icon: '💬',
          label: event.direction === 'inbound' ? 'SMS from customer' : 'SMS from staff',
          color: 'bg-blue-100 text-blue-700',
        };
      case 'call':
        return {
          icon: '📞',
          label: event.direction === 'inbound' ? 'Incoming call' : 'Outgoing call',
          color: 'bg-green-100 text-green-700',
        };
      case 'wixMessage':
        return {
          icon: '💬',
          label: 'Wix message',
          color: 'bg-purple-100 text-purple-700',
        };
      case 'system':
        return {
          icon: '🔔',
          label: 'System event',
          color: 'bg-gray-100 text-gray-700',
        };
      default:
        return {
          icon: '📬',
          label: 'Message',
          color: 'bg-gray-100 text-gray-700',
        };
    }
  }, []);

  /**
   * Resolve full item content from timeline event
   */
  const resolveTimelineEvent = useCallback(
    async (event: TimelineEvent): Promise<UnifiedInboxItem> => {
      const metadata = getEventMetadata(event);

      const item: UnifiedInboxItem = {
        id: event.id,
        customerId: event.customerId,
        type: event.type,
        direction: event.direction,
        preview: event.preview,
        timestamp: event.timestamp,
        timestamp_display: formatTimestamp(event.timestamp),
        channel: event.channel,
        staffId: event.staffId,
        ...metadata,
        systemMessage: event.type === 'system' ? event.preview : undefined,
      };

      // For now, we just use the timeline preview
      // In a full implementation, you'd fetch the full content from the specific collection
      return item;
    },
    [formatTimestamp, getEventMetadata]
  );

  /**
   * Fetch and subscribe to timeline
   */
  useEffect(() => {
    if (!customerId || !autoSubscribe) {
      return;
    }

    let mounted = true;
    let currentUnsub: (() => void) | null = null;

    Promise.resolve().then(() => {
      if (mounted) { setLoading(true); setError(null); }
    });

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startTimestamp = startDate.getTime();

      // Get the twilio-communications named database
      const db = getFirestore(firebaseApp, 'twilio-communications');

      // Query timeline from twilio-communications database
      const timelineQuery = query(
        collection(db, 'timeline'),
        where('customerId', '==', customerId),
        where('timestamp', '>=', startTimestamp),
        orderBy('timestamp', 'desc'),
        limit(resultLimit)
      ) as Query<DocumentData>;

      const subscribe = () => {
        if (!mounted) return;
        currentUnsub = onSnapshot(
          timelineQuery,
          async (snapshot) => {
            if (!mounted) return;
            try {
              const events = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              } as TimelineEvent));

              // Resolve each timeline event to full item
              const resolvedItems: UnifiedInboxItem[] = [];
              for (const event of events) {
                const item = await resolveTimelineEvent(event);
                resolvedItems.push(item);
              }

              setItems(resolvedItems);
              setLoading(false);
            } catch (err) {
              const errorMsg = err instanceof Error ? err.message : i18n.t('hooks.useUnifiedInbox.failedToResolveTimelineEvents');
              if (mounted) setError(errorMsg);
              if (mounted) setLoading(false);
            }
          },
          (err) => {
            const errorMsg = err instanceof Error ? err.message : i18n.t('hooks.useUnifiedInbox.failedToLoadUnifiedInbox');
            console.error('[useUnifiedInbox] Firestore error, resubscribing in 5s:', errorMsg);
            if (mounted) setError(errorMsg);
            if (mounted) setLoading(false);
            setTimeout(subscribe, 5000);
          }
        );
      };
      subscribe();

      return () => {
        mounted = false;
        currentUnsub?.();
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : i18n.t('hooks.useUnifiedInbox.failedToSetUpInboxSubscription');
      Promise.resolve().then(() => {
        if (!mounted) return;
        setError(errorMsg);
        setLoading(false);
      });
    }
  }, [customerId, days, resultLimit, autoSubscribe, resolveTimelineEvent]);

  /**
   * Manual refresh
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    // The subscription will automatically refresh
    // This is just to trigger a UI update
    await new Promise((resolve) => setTimeout(resolve, 500));
    setLoading(false);
  }, []);

  return {
    items,
    loading,
    error,
    isEmpty: items.length === 0 && !loading,
    refresh,
  };
}
